import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import { useIsMobile } from '@/shared/lib/media';
import { Combobox, type ComboboxOption, Icons, TextField } from '@/shared/ui';

import { type HelpLanguage, helpLanguageOf, loadHelpContent } from './content-loader';
import styles from './HelpCenter.module.css';
import { highlightParts, searchHelp } from './search';
import {
  DEFAULT_TOPIC,
  HELP_CATEGORIES,
  HELP_LINK_TARGETS,
  HELP_TOPICS,
  HELP_TOPIC_IDS,
  type HelpCategory,
  type HelpContent,
  type HelpTopicId,
  resolveHelpTopic,
} from './topics';

function useHelpContent(language: HelpLanguage): HelpContent | null {
  const [loaded, setLoaded] = useState<{ language: HelpLanguage; content: HelpContent } | null>(
    null,
  );
  useEffect(() => {
    let current = true;
    void loadHelpContent(language).then((content) => {
      if (current) setLoaded({ language, content });
    });
    return () => {
      current = false;
    };
  }, [language]);
  return loaded?.language === language ? loaded.content : null;
}

const categoryOf = (id: HelpTopicId): HelpCategory =>
  HELP_TOPICS.find((topic) => topic.id === id)?.category ?? 'start';

/** The text split by the search words, drawn with <mark> (React text nodes: nothing is parsed). */
function Marked({ text, query }: { text: string; query: string }) {
  return (
    <>
      {highlightParts(text, query).map((part, index) => (
        <Fragment key={index}>{part.match ? <mark>{part.text}</mark> : part.text}</Fragment>
      ))}
    </>
  );
}

/**
 * The in-app guide: a searchable contents (grouped by theme, collapsible) and the selected
 * article. On a phone the contents is one searchable picker instead of a long list. The article
 * is kept in the URL (`?topic=`), so a link to it can be shared inside the team; old names of
 * articles still open the right one. It explains how Planosfera behaves for everybody; it never
 * names a workspace, board or person.
 */
export function HelpCenter() {
  const { t, i18n } = useTranslation();
  const language = helpLanguageOf(i18n.language);
  const content = useHelpContent(language);
  const mobile = useIsMobile();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const heading = useRef<HTMLHeadingElement>(null);
  const requested = params.get('topic');
  const resolved = resolveHelpTopic(requested);
  const selectedId: HelpTopicId = resolved.kind === 'missing' ? DEFAULT_TOPIC : resolved.id;
  const selectedCategory = categoryOf(selectedId);
  // The category of the open article is open; the reader may open or fold the others, until
  // another article is opened (then the contents follows that article again).
  const [flipped, setFlipped] = useState<{
    topic: HelpTopicId;
    categories: ReadonlySet<HelpCategory>;
  }>({ topic: selectedId, categories: new Set() });
  const flips = flipped.topic === selectedId ? flipped.categories : new Set<HelpCategory>();
  const isOpen = (category: HelpCategory) =>
    (category === selectedCategory) !== flips.has(category);

  // An old or short name is replaced by the real address, so the bookmark updates itself.
  useEffect(() => {
    if (resolved.kind !== 'alias') return;
    const next = new URLSearchParams(params);
    next.set('topic', resolved.id);
    setParams(next, { replace: true });
  }, [resolved, params, setParams]);

  const searching = query.trim() !== '';
  const hits = useMemo(
    () => (content && searching ? searchHelp(content, HELP_TOPIC_IDS, query) : []),
    [content, query, searching],
  );

  // Choosing an article puts the reader at its title (keyboard and screen reader users too). A
  // search never takes the focus from the search field.
  const focusTitle = useRef(false);
  useEffect(() => {
    if (!focusTitle.current) return;
    focusTitle.current = false;
    heading.current?.focus();
  }, [selectedId, searching]);

  const hrefOf = (id: HelpTopicId) => {
    const next = new URLSearchParams(params);
    next.set('topic', id);
    return { search: `?${next.toString()}` };
  };
  const choose = () => {
    focusTitle.current = true;
    setQuery('');
  };

  const article = content?.[selectedId] ?? null;
  const meta = HELP_TOPICS.find((topic) => topic.id === selectedId);
  const categoryName = (category: HelpCategory) => t(`helpCenter.categories.${category}`);

  const options: ComboboxOption[] = content
    ? HELP_TOPICS.map((topic) => ({
        value: topic.id,
        label: content[topic.id].title,
        group: categoryName(topic.category),
      }))
    : [];

  const nav = content && !mobile && (
    <nav className={styles.toc} aria-label={t('helpCenter.contents')}>
      {HELP_CATEGORIES.map((category) => {
        const items = HELP_TOPICS.filter((item) => item.category === category);
        const open = isOpen(category);
        const listId = `help-group-${category}`;
        return (
          <div key={category} className={styles.group}>
            <h2 className={styles.groupTitle}>
              <button
                type="button"
                className={styles.groupButton}
                aria-expanded={open}
                aria-controls={listId}
                onClick={() => {
                  const next = new Set(flips);
                  if (next.has(category)) next.delete(category);
                  else next.add(category);
                  setFlipped({ topic: selectedId, categories: next });
                }}
              >
                {open ? <Icons.chevronDown /> : <Icons.chevronRight />}
                <span>{categoryName(category)}</span>
                <span className={styles.count}>{items.length}</span>
              </button>
            </h2>
            <ul id={listId} className={styles.list} hidden={!open}>
              {items.map((item) => (
                <li key={item.id}>
                  <Link
                    to={hrefOf(item.id)}
                    className={`${styles.topic} ${item.id === selectedId && !searching ? styles.topicOn : ''}`}
                    aria-current={item.id === selectedId && !searching ? 'page' : undefined}
                    onClick={choose}
                  >
                    {content[item.id].title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </nav>
  );

  const results = searching && content && (
    <section className={styles.results} aria-label={t('helpCenter.resultsTitle')}>
      <div className={styles.resultsHead}>
        <p role="status" className={styles.resultsCount}>
          {hits.length === 0
            ? t('helpCenter.noResults')
            : t('helpCenter.resultsCount', { count: hits.length, query: query.trim() })}
        </p>
        <button
          type="button"
          className={styles.clear}
          onClick={() => {
            setQuery('');
          }}
        >
          {t('helpCenter.clearSearch')}
        </button>
      </div>
      {hits.length === 0 ? (
        <p className={styles.empty}>{t('helpCenter.noResultsHint')}</p>
      ) : (
        <ol className={styles.hits}>
          {hits.map((hit) => (
            <li key={hit.id} className={styles.hit}>
              <span className={styles.hitCategory}>{categoryName(categoryOf(hit.id))}</span>
              <Link to={hrefOf(hit.id)} className={styles.hitTitle} onClick={choose}>
                <Marked text={content[hit.id].title} query={query} />
              </Link>
              <span className={styles.hitExcerpt}>
                <Marked text={hit.excerpt} query={query} />
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );

  const body =
    article && meta ? (
      <article className={styles.article} data-testid="help-article">
        <p className={styles.kicker}>{categoryName(meta.category)}</p>
        <h2 ref={heading} tabIndex={-1} className={styles.articleTitle}>
          {article.title}
        </h2>
        {resolved.kind === 'missing' && (
          <p role="status" className={styles.notice}>
            {t('helpCenter.unknownTopic', { topic: requested ?? '', title: article.title })}
          </p>
        )}
        <p className={styles.lead}>{article.lead}</p>
        {article.steps && (
          <section className={styles.section}>
            <h3>{t('helpCenter.steps')}</h3>
            <ol>
              {article.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          </section>
        )}
        {article.points && (
          <section className={styles.section}>
            <h3>{t('helpCenter.points')}</h3>
            <ul>
              {article.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </section>
        )}
        {article.example && (
          <section className={styles.section}>
            <h3>{t('helpCenter.example')}</h3>
            <p className={styles.example}>{article.example}</p>
          </section>
        )}
        {'links' in meta && meta.links.length > 0 && (
          <section className={styles.section}>
            <h3>{t('helpCenter.tryIt')}</h3>
            <div className={styles.links}>
              {meta.links.map((key) => (
                <Link key={key} to={HELP_LINK_TARGETS[key]} className={styles.chip}>
                  {t(`helpCenter.links.${key}`)}
                </Link>
              ))}
            </div>
          </section>
        )}
        {'related' in meta && meta.related.length > 0 && (
          <section className={styles.section}>
            <h3>{t('helpCenter.related')}</h3>
            <div className={styles.links}>
              {meta.related.map((id) => {
                const known = resolveHelpTopic(id);
                return known.kind === 'topic' ? (
                  <Link key={id} to={hrefOf(known.id)} className={styles.chip} onClick={choose}>
                    {content?.[known.id].title}
                  </Link>
                ) : null;
              })}
            </div>
          </section>
        )}
      </article>
    ) : null;

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div className={styles.headText}>
          <h1 className={styles.title}>{t('helpCenter.title')}</h1>
          <p className={styles.subtitle}>{t('helpCenter.subtitle')}</p>
        </div>
        <div className={styles.search}>
          <TextField
            label={t('helpCenter.search')}
            type="search"
            value={query}
            placeholder={t('helpCenter.searchPlaceholder')}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
        </div>
      </header>
      {!content && (
        <p className={styles.empty} role="status">
          {t('common.loading')}
        </p>
      )}
      {content && mobile && (
        <div className={styles.picker}>
          <Combobox
            label={t('helpCenter.articlePicker')}
            options={options}
            value={selectedId}
            limit={HELP_TOPICS.length}
            searchPlaceholder={t('helpCenter.searchPlaceholder')}
            emptyText={t('helpCenter.noResults')}
            onChange={(id) => {
              const known = resolveHelpTopic(id);
              if (known.kind !== 'topic') return;
              focusTitle.current = true;
              setQuery('');
              const next = new URLSearchParams(params);
              next.set('topic', known.id);
              setParams(next);
            }}
          />
        </div>
      )}
      <div className={styles.body}>
        {nav}
        <div className={styles.main}>{searching ? results : body}</div>
      </div>
    </div>
  );
}
